import { getPageViewportParams } from "./pdfPageViewport";
import type { PdfZoomState } from "./pdfZoom";

export const PAGE_RENDER_BUFFER = 3;

export type PageCssDimensions = {
  width: number;
  height: number;
};

export const getPageCssDimensions = (
  page: any,
  pdfZoom: PdfZoomState,
  viewerWidth: number,
  viewerHeight: number,
  currentAngle: number
): PageCssDimensions => {
  const params = getPageViewportParams(page, pdfZoom, viewerWidth, viewerHeight, currentAngle);
  return { width: params.cssWidth, height: params.cssHeight };
};

export const buildPageOffsets = (
  totalPages: number,
  pageDimensions: Record<number, PageCssDimensions>,
  fallbackHeight: number
): number[] => {
  const offsets: number[] = [0];
  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    const height = pageDimensions[pageNo]?.height ?? fallbackHeight;
    offsets[pageNo] = offsets[pageNo - 1] + height + 1;
  }
  return offsets;
};

export const getVisiblePageRange = (
  scrollTop: number,
  viewerHeight: number,
  pageOffsets: number[],
  totalPages: number,
  buffer = PAGE_RENDER_BUFFER
) => {
  if (totalPages < 1 || pageOffsets.length < 2) {
    return { first: 1, last: Math.max(1, totalPages) };
  }

  const viewportBottom = scrollTop + viewerHeight;
  let first = 1;
  let last = 1;

  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    const pageTop = pageOffsets[pageNo - 1] ?? 0;
    const pageBottom = pageOffsets[pageNo] ?? pageTop;
    if (pageBottom >= scrollTop && first === 1 && pageTop <= viewportBottom) {
      first = pageNo;
    }
    if (pageTop <= viewportBottom) {
      last = pageNo;
    }
    if (pageTop > viewportBottom) {
      break;
    }
  }

  return {
    first: Math.max(1, first - buffer),
    last: Math.min(totalPages, last + buffer)
  };
};

export const getVisiblePageFromOffsets = (
  scrollTop: number,
  pageOffsets: number[],
  totalPages: number
) => {
  let visiblePage = 1;
  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    const pageTop = pageOffsets[pageNo - 1] ?? 0;
    if (pageTop - 12 <= scrollTop) {
      visiblePage = pageNo;
    } else {
      break;
    }
  }
  return visiblePage;
};

export const getVisiblePageFromPageTops = (
  scrollTop: number,
  pageTops: number[],
  totalPages: number
) => {
  let visiblePage = 1;
  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    const pageTop = pageTops[pageNo - 1];
    if (pageTop == null) break;
    if (pageTop - 12 <= scrollTop) {
      visiblePage = pageNo;
    } else {
      break;
    }
  }
  return visiblePage;
};

export const getVisiblePageFromContainers = (
  scrollTop: number,
  getPageTop: (pageNo: number) => number | null | undefined,
  totalPages: number
): number | null => {
  const pageTops: number[] = [];
  for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
    const pageTop = getPageTop(pageNo);
    if (pageTop == null) {
      return pageTops.length ? getVisiblePageFromPageTops(scrollTop, pageTops, pageTops.length) : null;
    }
    pageTops.push(pageTop);
  }
  return getVisiblePageFromPageTops(scrollTop, pageTops, totalPages);
};

export const getPageScrollTopFromOffsets = (pageNo: number, pageOffsets: number[]) =>
  Math.max(0, (pageOffsets[pageNo - 1] ?? 0) - 8);

export const expandPageRange = (
  first: number,
  last: number,
  priorityPage: number,
  totalPages: number,
  buffer = PAGE_RENDER_BUFFER
) => {
  const min = Math.max(1, Math.min(first, priorityPage) - buffer);
  const max = Math.min(totalPages, Math.max(last, priorityPage) + buffer);
  return { first: min, last: max };
};

export const getPagesInRange = (first: number, last: number, priorityPage?: number) => {
  const pages: number[] = [];
  if (priorityPage != null && priorityPage >= first && priorityPage <= last) {
    pages.push(priorityPage);
  }
  for (let pageNo = first; pageNo <= last; pageNo += 1) {
    if (pageNo !== priorityPage) {
      pages.push(pageNo);
    }
  }
  return pages;
};
